/**
 * LPWEB-16416 — Paid Social PDP Carousel: Inspiration & Gifts Titles Only
 *
 * Collection: gifts-inspiration
 * Reference PDP: /products/where-to-go-when-with-kids
 *
 * Flow:
 *  1. Detect paid social traffic (UTM params, fbclid, ttclid)
 *  2. Persist detection to sessionStorage for session duration
 *  3. On qualifying Inspiration & Gifts PDPs, override the carousel
 *     with top-seller handles sorted list — matching DOM items by handle
 *  4. Fallback silently to default carousel if < 3 products matched
 */

(function () {
  'use strict';

  const SESSION_KEY  = 'lp:paid_social_session';
  const MIN_PRODUCTS = 3;
  const COLLECTION   = 'gifts-inspiration';

  // ── 1. Paid Social Detection ──────────────────────────────────────────────────

  function isPaidSocialUrl() {
    try {
      const p      = new URLSearchParams(window.location.search);
      const medium = (p.get('utm_medium') || '').toLowerCase().replace(/[-_\s]/g, '');
      const source = (p.get('utm_source') || '').toLowerCase();

      const MEDIUMS = ['paidsocial', 'social', 'cpc', 'paid'];
      const SOURCES = ['facebook', 'instagram', 'meta', 'tiktok', 'pinterest', 'snapchat'];

      if (MEDIUMS.includes(medium))                              return true;
      if (medium && SOURCES.some(s => source.includes(s)))      return true;
      if (p.has('fbclid') || p.has('ttclid') || p.has('igshid')) return true;
    } catch (_) {}
    return false;
  }

  function initSession() {
    try {
      if (isPaidSocialUrl()) sessionStorage.setItem(SESSION_KEY, '1');
      return sessionStorage.getItem(SESSION_KEY) === '1';
    } catch (_) {
      return isPaidSocialUrl();
    }
  }

  // ── 2. Config from Liquid Data Bridge ────────────────────────────────────────

  function getConfig() {
    const el = document.getElementById('paid-social-gni-data');
    if (!el) return null;
    try {
      const cfg = JSON.parse(el.textContent);
      if (window.themeVariables?.routes?.productRecommendationsUrl) {
        cfg.recsUrl = window.themeVariables.routes.productRecommendationsUrl;
      }
      return cfg;
    } catch (e) {
      console.warn('[LPWEB-16416] Config parse error', e);
      return null;
    }
  }

  // ── 3. Sort DOM items by top-seller handle order ──────────────────────────────

  /**
   * Sorts existing DOM wrapper elements by the merchandising top-seller rank.
   * Items whose handle matches a topHandle are promoted in rank order.
   * Items not in the top-seller list are appended after in their original order.
   *
   * @param {HTMLElement[]} items   - .js-rec-product-wrapper elements from the DOM
   * @param {string[]} topHandles  - Ordered handles from Customizer (index 0 = #1 seller)
   * @returns {HTMLElement[]}
   */
  function sortByTopSellers(items, topHandles) {
    if (!topHandles?.length) return items;

    const ranked   = [];
    const unranked = [];

    items.forEach(item => {
      const handle = (item.dataset.productHandle || '').trim().toLowerCase();
      const idx    = topHandles.indexOf(handle);
      idx === -1 ? unranked.push(item) : ranked.push({ item, idx });
    });

    ranked.sort((a, b) => a.idx - b.idx);
    return [...ranked.map(r => r.item), ...unranked];
  }

  /**
   * Checks whether a DOM item belongs to the Inspiration & Gifts collection.
   * Used only for automatic recommendations (Case B) where we need to filter
   * out non-G&I products from Shopify's generic recommendation results.
   *
   * @param {HTMLElement} el
   * @returns {boolean}
   */
  function isGiftsInspirationItem(el) {
    const col  = (el.dataset.productCollection || '').toLowerCase();
    const tags = (el.dataset.productTags       || '').toLowerCase();
    const type = (el.dataset.productType       || '').toLowerCase();

    if (col.includes('gifts-inspiration'))  return true;
    if (tags.includes('gifts-inspiration')) return true;

    const GI_TYPES = [
      'inspiration & gifts',
      'inspiration and gifts',
      'gifts & inspiration',
      'gifts and inspiration',
      'gifts-inspiration'
    ];
    return GI_TYPES.some(t => type.includes(t));
  }

  // ── 4. DOM Override ──────────────────────────────────────────────────────────

  /**
   * Reorders the carousel scroller by moving nodes — does NOT use innerHTML.
   * Moving existing nodes preserves custom element lifecycle, event listeners,
   * and all existing CSS applied to <product-item> elements.
   *
   * @param {HTMLElement} carouselEl
   * @param {{items: HTMLElement[], source: string}} result
   * @returns {boolean}
   */
  function applyOverride(carouselEl, result) {
    if (!result || result.items.length < MIN_PRODUCTS) return false;

    const scroller =
      carouselEl.querySelector('.product-list__inner--scroller') ||
      carouselEl.querySelector('.product-list__inner');

    if (!scroller) return false;

    // Move nodes in sorted order — appendChild on an existing node moves it,
    // preserving all attached styles and event listeners
    result.items.forEach(item => scroller.appendChild(item));

    // Annotate for analytics / Tagular
    Object.assign(carouselEl.dataset, {
      gniCarousel: 'true',
      gniSource:   result.source,
      gniCount:    String(result.items.length)
    });

    return true;
  }

  // ── 5. HTML Parsing (Case B only) ────────────────────────────────────────────

  function parseItems(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    // Primary: LPWEB-16416 wrappers with metadata attributes
    let items = Array.from(doc.querySelectorAll('.js-rec-product-wrapper[data-product-handle]'));

    // Fallback selectors
    if (!items.length) items = Array.from(doc.querySelectorAll('[data-product-handle]'));
    if (!items.length) {
      const s = doc.querySelector('.product-list__inner--scroller');
      if (s) items = Array.from(s.children);
    }
    return items;
  }

  // ── 6. Fetch G&I Products (Case B only) ─────────────────────────────────────

  async function fetchGiftsInspirationProducts(cfg) {
    const { productId, sectionId, topSellerHandles, recsUrl } = cfg;
    if (!recsUrl) { console.warn('[LPWEB-16416] recsUrl missing'); return null; }

    // Primary: Shopify recommendations API — filter to G&I, then sort
    try {
      const url  = `${recsUrl}?product_id=${productId}&section_id=${sectionId}&limit=12&intent=related`;
      const resp = await fetch(url);
      if (resp.ok) {
        const all      = parseItems(await resp.text());
        const filtered = all.filter(isGiftsInspirationItem);
        const sorted   = sortByTopSellers(filtered, topSellerHandles);
        if (sorted.length >= MIN_PRODUCTS) return { items: sorted, source: 'recommendations-api' };
      }
    } catch (e) { console.warn('[LPWEB-16416] Recs API failed', e); }

    // Secondary: Collection JSON endpoint as fallback
    try {
      const url  = `/collections/${COLLECTION}/products.json?limit=12&sort_by=best-selling`;
      const resp = await fetch(url);
      if (resp.ok) {
        const data = await resp.json();
        const els  = (data.products || []).map(p => {
          const div = document.createElement('div');
          div.className                   = 'js-rec-product-wrapper';
          div.dataset.productHandle       = p.handle;
          div.dataset.productCollection   = COLLECTION;
          div.dataset.productType         = p.product_type || '';
          div.dataset.productTags         = (p.tags || []).join(',').toLowerCase();
          div.innerHTML                   = `<a href="/products/${p.handle}">${p.title}</a>`;
          return div;
        });
        const sorted = sortByTopSellers(els, topSellerHandles);
        if (sorted.length >= MIN_PRODUCTS) return { items: sorted, source: 'collection-api' };
      }
    } catch (e) { console.warn('[LPWEB-16416] Collection API failed', e); }

    return null;
  }

  // ── 7. Main ──────────────────────────────────────────────────────────────────

  async function init() {
    // Product pages only
    if (window.themeVariables?.settings?.pageType !== 'product') return;

    // Paid social session only
    if (!initSession()) return;

    // Inspiration & Gifts PDP only
    const cfg = getConfig();
    if (!cfg?.isGiftsInspirationProduct) return;

    const carouselEl = document.querySelector('product-recommendations');
    if (!carouselEl) return;

    // ── Case A: items already pre-rendered in the DOM ─────────────────────────
    // When section.settings.products is configured (manual list), Liquid renders
    // items server-side. No async fetch occurs — content exists on page load.
    // We sort the existing items by top-seller rank using handle matching.
    const existingScroller = carouselEl.querySelector('.product-list__inner--scroller');
    const existingItems    = existingScroller
      ? Array.from(existingScroller.querySelectorAll('.js-rec-product-wrapper'))
      : [];

    if (existingItems.length > 0) {
      // Sort pre-rendered items by top-seller rank — no filtering needed
      // since Customizer manual list is already scoped to G&I products
      const sorted  = sortByTopSellers(existingItems, cfg.topSellerHandles);
      const applied = applyOverride(carouselEl, { items: sorted, source: 'pre-rendered-manual' });

      if (applied) {
        document.dispatchEvent(new CustomEvent('lp:gni-carousel-applied', {
          detail: { productId: cfg.productId, count: sorted.length, source: 'pre-rendered-manual' }
        }));
        console.info('[LPWEB-16416] Override applied on pre-rendered items ✓', sorted.length, 'items');
      } else {
        console.info('[LPWEB-16416] Pre-rendered items found but fewer than', MIN_PRODUCTS, '— fallback.');
      }
      return; // No need for the MutationObserver in this case
    }

    // ── Case B: automatic recommendations (async fetch by Shopify) ────────────
    // The <product-recommendations> custom element hydrates asynchronously.
    // Use MutationObserver to wait for content before applying the override.
    const observer = new MutationObserver(async (_, obs) => {
      const scroller = carouselEl.querySelector('.product-list__inner--scroller');
      if (!scroller?.children.length) return;
      obs.disconnect();

      const result     = await fetchGiftsInspirationProducts(cfg);
      const overridden = applyOverride(carouselEl, result);

      if (overridden) {
        document.dispatchEvent(new CustomEvent('lp:gni-carousel-applied', {
          detail: { productId: cfg.productId, count: result.items.length, source: result.source }
        }));
        console.info('[LPWEB-16416] Carousel overridden ✓', result.source, result.items.length, 'items');
      } else {
        console.info(
          '[LPWEB-16416] Fallback to default carousel.',
          result
            ? `Only ${result.items?.length ?? 0} items (min: ${MIN_PRODUCTS})`
            : 'No result returned.'
        );
      }
    });

    observer.observe(carouselEl, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 10000); // Safety cleanup after 10s
  }

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init)
    : init();

})();